import {
  useRouteBodyEditorSelection,
  useRouteBodyState,
  useRouteBodyRef,
} from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { PinyinMnemonicTextRenderer } from "@-/dev.improve.haniverse.routes.nav.app._components.pinyin-mnemonic-text-renderer";
import { MorphemeMnemonicsPronunciationViewTabEditButton } from "#morpheme-mnemonics-pronunciation-view-tab-edit-button";
import { MorphemeMnemonicsPronunciationViewLayout } from "#morpheme-mnemonics-pronunciation-view-layout";
import { getGraphemePronunciationImageUrlPath } from "@-/dev.improve.haniverse.assets.graphemes.pinyin.images/public";
import { MorphemeMnemonicsPanelContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content._context";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { GraphemeMorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { usePrevalentPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.pinyin.prevalence";
import { actorQueries } from "@-/dev.improve.haniverse.database.schemas.actor.query";
import {
  useResetGraphemePronunciationCard,
  useUpsertGraphemePronunciationCard,
} from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.pronunciation.update";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useEffect, useRef } from "react";
import { useIsPremium, useIsSignedIn } from "@-/dev.improve.haniverse.components.superwall";
import {
  buildAbsentMnemonicPrompt,
  buildPremiumLockedPrompt,
  buildRevealedMnemonicUpsell,
  resolveMnemonicGateState,
  shouldShowRevealedMnemonicUpsell,
} from "@-/dev.improve.haniverse.lib.morpheme.mnemonic-gate";
import { useIsMnemonicGateReferenceSurface } from "@-/dev.improve.haniverse.lib.morpheme.mnemonic-gate-surface";
import { Image } from "@-/dev.improve.haniverse.components.ui.image";
import { FlashcardScrollView } from "@-/dev.improve.haniverse.components.ui.card-scroll-view";
import { useCardScrollOptIn } from "@-/dev.improve.haniverse.lib.cards.review-swipe";
import { Pressable, ScrollView, TextInput, View } from "@-/com.npmjs.react-native";
import { LongPressable } from "@-/dev.improve.haniverse.components.ui.long-pressable";
import { KeyboardAvoidingView } from "@-/com.npmjs.react-native-keyboard-controller";
import { useAdminAccess } from "@-/dev.improve.haniverse.lib.admin.access";

/** Body shown above the fixed pinyin tab row when a reading is selected (excludes the tab strip). */
export function MorphemeMnemonicsPronunciationViewTab({
  selectedPinyin,
}: {
  selectedPinyin: string;
}) {
  const isAdmin = useAdminAccess();
  const [grapheme, pinyinList, isDescriptionPremium] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (c) => c.readonlySlice.data.grapheme,
    (c) => c.readonlySlice.data.pinyinList,
    // Pronunciation cards carry no premium flag of their own; the grapheme-level flag governs
    // both mnemonic kinds, matching how the build strips them.
    (c) => c.readonlySlice.data.isDescriptionPremium,
  );
  const studyPinyinList = usePrevalentPinyin(grapheme, pinyinList);
  const matchedStudyPinyin = studyPinyinList.find(
    ({ toneNumbersPinyin }) => toneNumbersPinyin === selectedPinyin,
  );
  const [isEditing, setIsEditing] = useContextSelectors(
    MorphemeMnemonicsPanelContext,
    (c) => c.isEditing,
    (c) => c.setIsEditing,
  );
  const inputRef = useRef<TextInput | null>(null);
  const editorSelection = useRouteBodyEditorSelection(inputRef, "mnemonic-editor");
  const draftRef = useRouteBodyRef("mnemonic-draft", "");
  const wasEditingRef = useRouteBodyRef("mnemonic-was-editing", false);
  const isPremium = useIsPremium();
  const isSignedIn = useIsSignedIn();
  const [hasRevealed, setHasRevealed] = useRouteBodyState("mnemonic-revealed", false);
  const isReferenceSurface = useIsMnemonicGateReferenceSurface();
  const scrollRef = useRef<ScrollView>(null);
  const { enableScroll } = useCardScrollOptIn();
  useEffect(() => {
    if (isEditing) {
      enableScroll();
    }
  }, [enableScroll, isEditing]);
  // Grapheme images are experimental and off by default.
  const [isGraphemeImagesEnabled] = useDeviceSetting("isGraphemeImagesEnabled");
  const pronunciationImageUrlPath = isGraphemeImagesEnabled
    ? getGraphemePronunciationImageUrlPath(grapheme, selectedPinyin)
    : null;
  const { data: pronunciationActorCard } = useQuery(
    actorQueries(import.meta.location).userGraphemePronunciationCard.findFirst.useQueryOptions({
      where: { grapheme, pinyin: selectedPinyin },
      with: { userCard: true },
    }),
  );
  const upsertGraphemePronunciationCard = useUpsertGraphemePronunciationCard();
  const resetGraphemePronunciationCard = useResetGraphemePronunciationCard();
  const isSaving =
    upsertGraphemePronunciationCard.isPending || resetGraphemePronunciationCard.isPending;
  const saveError = upsertGraphemePronunciationCard.error ?? resetGraphemePronunciationCard.error;
  const startEditing = () => {
    if (!isAdmin) {
      return;
    }
    upsertGraphemePronunciationCard.reset();
    resetGraphemePronunciationCard.reset();
    setIsEditing(true);
  };
  const stopEditing = () => {
    void inputRef.current?.blur();
    setIsEditing(false);
  };

  const customMnemonic = pronunciationActorCard?.description?.trim() ?? "";
  const catalogMnemonic = matchedStudyPinyin?.mnemonic?.trim() ?? "";
  const mnemonic = customMnemonic.length > 0 ? customMnemonic : catalogMnemonic;
  const gateState = resolveMnemonicGateState({
    hasCatalogMnemonic: catalogMnemonic.length > 0,
    hasOwnMnemonic: customMnemonic.length > 0,
    hasRevealed,
    isDescriptionPremium,
    isPremium,
    isReferenceSurface,
    isSignedIn,
  });

  // Never seed the editor from a gated mnemonic — that would hand the withheld text straight
  // back through the input. Gated states start from an empty draft.
  const editableMnemonic =
    gateState === "premium-locked" || gateState === "revealable" ? "" : mnemonic;

  useEffect(() => {
    if (isEditing && !wasEditingRef.current) {
      draftRef.current = editableMnemonic;
    }
    wasEditingRef.current = isEditing;
  }, [editableMnemonic, isEditing, draftRef, wasEditingRef]);

  let mnemonicBody;
  if (isAdmin && isEditing) {
    const handleSave = () => {
      upsertGraphemePronunciationCard.mutate(
        { description: draftRef.current, grapheme, pinyin: selectedPinyin },
        { onSuccess: stopEditing },
      );
    };
    const handleCancel = () => {
      stopEditing();
    };
    const handleReset = () => {
      resetGraphemePronunciationCard.mutate(
        { grapheme, pinyin: selectedPinyin },
        { onSuccess: stopEditing },
      );
    };
    mnemonicBody = (
      <View className="gap-2">
        <View className="rounded-xs border border-border bg-muted/30 p-1">
          <TextInput
            accessibilityLabel="Mnemonic Markdown"
            autoFocus
            className="min-h-[120px] p-2 text-[14px] leading-7 text-foreground"
            defaultValue={wasEditingRef.current ? draftRef.current : editableMnemonic}
            editable={!isSaving}
            multiline
            onChangeText={(markdown) => {
              draftRef.current = markdown;
            }}
            onLayout={editorSelection.onLayout}
            onSelectionChange={({ nativeEvent }) => {
              editorSelection.onChangeSelection(nativeEvent.selection);
            }}
            placeholder="Write your mnemonic for this reading. Markdown is shown as plain text."
            ref={inputRef}
            testID="mnemonic-markdown-input"
            textAlignVertical="top"
          />
        </View>
        {saveError ? (
          <Text accessibilityRole="alert" className="text-sm text-destructive">
            Could not save mnemonic. {saveError.message}
          </Text>
        ) : null}
        <View className="flex-row justify-end gap-1">
          <Button disabled={isSaving} onPress={handleReset} size="sm" variant="ghost">
            <Text>Reset</Text>
          </Button>
          <Button disabled={isSaving} onPress={handleCancel} size="sm" variant="ghost">
            <Text>Cancel</Text>
          </Button>
          <Button disabled={isSaving} onPress={handleSave} size="sm" variant="ghost">
            <Text>{isSaving ? "Saving…" : "Save"}</Text>
          </Button>
        </View>
      </View>
    );
  } else if (gateState === "revealable") {
    // Free reading mnemonic, present on the device but hidden until a guest asks for it.
    // Reference page only — the panels people use in the app never gate it.
    mnemonicBody = (
      <View className="gap-2">
        <Button
          onPress={() => {
            setHasRevealed(true);
          }}
          size="sm"
          testID="reveal-pronunciation-mnemonic-button"
        >
          <Text>Reveal mnemonic</Text>
        </Button>
      </View>
    );
  } else if (gateState === "premium-locked") {
    // Premium reading mnemonic: stripped from the client database, so there is nothing to reveal.
    mnemonicBody = (
      <View className="gap-2">
        <Text
          className="text-[14px] leading-7"
          colorVariant="muted"
          testID="premium-pronunciation-mnemonic-prompt"
        >
          {buildPremiumLockedPrompt(grapheme)}
        </Text>
      </View>
    );
  } else if (mnemonic) {
    mnemonicBody = (
      <View className="gap-2">
        <PinyinMnemonicTextRenderer
          className="text-[14px] leading-7 text-foreground"
          content={mnemonic}
          showToneHelp
          toneNumbersPinyin={selectedPinyin}
        />
        {/* Bound to the reveal button: no button offered on this surface, no pitch. */}
        {shouldShowRevealedMnemonicUpsell({ gateState, isReferenceSurface, isSignedIn }) ? (
          <Text
            className="text-[13px] leading-6 text-muted-foreground"
            testID="reveal-pronunciation-mnemonic-upsell"
          >
            {buildRevealedMnemonicUpsell()}
          </Text>
        ) : null}
      </View>
    );
  } else {
    mnemonicBody = (
      <View className="gap-2">
        <Text className="text-[14px] leading-7 italic" colorVariant="muted">
          No mnemonic yet.
        </Text>
        {isSignedIn ? null : (
          <Text
            className="text-[13px] leading-6 text-muted-foreground"
            testID="absent-pronunciation-mnemonic-prompt"
          >
            {buildAbsentMnemonicPrompt(grapheme)}
          </Text>
        )}
      </View>
    );
  }

  return (
    <KeyboardAvoidingView behavior="padding" className="relative flex-1 bg-surface">
      <FlashcardScrollView
        className="flex-1"
        // The page surfaces sit 9px from the device edges, matching the glyph tab.
        contentContainerClassName="gap-1 px-2.25 py-1"
        ref={scrollRef}
      >
        <MorphemeMnemonicsPronunciationViewLayout selectedPinyin={selectedPinyin}>
          <LongPressable
            accessible={false}
            className="min-w-0"
            onLongPress={isAdmin && !isEditing ? startEditing : undefined}
            testID="pronunciation-panel-mnemonic-column"
          >
            {mnemonicBody}
          </LongPressable>
        </MorphemeMnemonicsPronunciationViewLayout>
        {pronunciationImageUrlPath !== null ? (
          <Pressable
            accessibilityLabel="Expand image"
            accessibilityRole="button"
            className="aspect-square w-full"
            onPress={() => {
              enableScroll();
              scrollRef.current?.scrollToEnd({ animated: true });
            }}
          >
            <Image
              className="size-full"
              contentFit="contain"
              source={{ uri: pronunciationImageUrlPath }}
            />
          </Pressable>
        ) : null}
      </FlashcardScrollView>
      {isEditing || !isAdmin ? null : (
        <View className="absolute right-3 bottom-3 rounded-full bg-background/80 p-2 shadow-sm">
          <MorphemeMnemonicsPronunciationViewTabEditButton />
        </View>
      )}
    </KeyboardAvoidingView>
  );
}
